import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * Query History — keeps the last 100 executed queries in localStorage for fast
 * "run again / favorite / delete", and writes an immutable audit record of every
 * execution to the `studio_query_history` Firestore collection.
 */

export const QUERY_HISTORY_COLLECTION = "studio_query_history";
const HISTORY_KEY = "firestoreAdmin.queryHistory.v1";
const MAX_HISTORY = 100;

function loadAll() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistAll(list) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota */
  }
}

function makeId() {
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Serialize filters to a plain, storable shape (no Firestore snapshots). */
function serializeFilters(filters) {
  return (filters || [])
    .filter((f) => f.field && f.op)
    .map((f) => ({ field: f.field, op: f.op, value: f.value ?? "", type: f.type || "string" }));
}

export function listQueryHistory() {
  return loadAll().sort((a, b) => {
    if (Boolean(b.favorite) !== Boolean(a.favorite)) return b.favorite ? 1 : -1;
    return (b.createdAt || "").localeCompare(a.createdAt || "");
  });
}

/**
 * Record a query execution. Trims to the last 100 (favorites are always kept)
 * and mirrors an audit entry into Firestore.
 */
export function recordQueryExecution(entry) {
  const record = {
    id: makeId(),
    collection: entry.collection || "",
    filters: serializeFilters(entry.filters),
    advancedSearch: entry.advancedSearch || null,
    mode: entry.mode || "client",
    executionTimeMs: entry.executionTimeMs ?? 0,
    resultCount: entry.resultCount ?? 0,
    createdAt: new Date().toISOString(),
    favorite: false,
  };

  const list = loadAll();
  list.unshift(record);

  // Keep favorites + newest up to MAX_HISTORY.
  const favorites = list.filter((r) => r.favorite);
  const rest = list.filter((r) => !r.favorite).slice(0, Math.max(0, MAX_HISTORY - favorites.length));
  persistAll([...favorites, ...rest].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")));

  return record;
}

export function deleteQueryHistory(id) {
  persistAll(loadAll().filter((r) => r.id !== id));
}

export function toggleQueryFavorite(id) {
  persistAll(loadAll().map((r) => (r.id === id ? { ...r, favorite: !r.favorite } : r)));
}

export function clearQueryHistory() {
  persistAll(loadAll().filter((r) => r.favorite));
}

/**
 * Immutable audit sink. Best-effort — never throws into the query flow.
 * @returns {Promise<string|null>} document id or null on failure
 */
export async function auditQueryExecution(entry) {
  try {
    const ref = await addDoc(collection(db, QUERY_HISTORY_COLLECTION), {
      user: entry.user || "",
      userUid: entry.userUid || "",
      collection: entry.collection || "",
      filters: serializeFilters(entry.filters),
      advancedSearch: entry.advancedSearch || null,
      mode: entry.mode || "client",
      executionTimeMs: entry.executionTimeMs ?? 0,
      resultCount: entry.resultCount ?? 0,
      createdAt: new Date().toISOString(),
      serverAt: serverTimestamp(),
    });
    return ref.id;
  } catch {
    return null;
  }
}
